import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

/** Keep the last result in place while its replacement is being fetched. */
export default function AsyncPanel({
  busy,
  hasData,
  error = false,
  children,
  skeleton = <PanelSkeleton />,
  className = "",
  loadingLabel,
  updatingLabel,
  transitionKey,
}: {
  busy: boolean;
  hasData: boolean;
  error?: boolean;
  children: ReactNode;
  skeleton?: ReactNode;
  className?: string;
  loadingLabel: string;
  updatingLabel: string;
  transitionKey?: string | number;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const lastKey = useRef(transitionKey);
  const [settledHeight, setSettledHeight] = useState(0);
  const reducedMotion = useReducedMotion();
  const showSkeleton = busy && !hasData && !error;

  useEffect(() => {
    if (busy || !hasData || error) return;
    const changed = lastKey.current !== transitionKey;
    lastKey.current = transitionKey;
    const content = contentRef.current;
    if (!changed || reducedMotion || !content?.animate) return;
    // Animate in place: remounting by response key would discard keyboard focus.
    const animation = content.animate([{ opacity: 0.78 }, { opacity: 1 }], { duration: 200, easing: "ease-out" });
    return () => animation.cancel();
  }, [busy, hasData, error, transitionKey, reducedMotion]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || busy || error || !hasData) return;
    const measure = () => setSettledHeight(content.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [busy, error, hasData, transitionKey]);

  return (
    <div className={`a1-async-panel ${busy ? "is-busy" : ""} ${hasData ? "has-data" : ""} ${error ? "is-error" : ""} ${className}`}>
      {/* Outside the busy region so its announcement is not deferred. */}
      <div role="status" aria-live="polite" aria-atomic="true" className={`a1-async-status ${busy ? "is-visible" : ""}`}>
        {busy && <><span className="a1-async-dot" aria-hidden="true" />{hasData ? updatingLabel : loadingLabel}</>}
      </div>
      <div aria-busy={busy} className="a1-async-content" style={busy && settledHeight ? { minHeight: settledHeight } : undefined}>
        {showSkeleton ? skeleton : <div ref={contentRef} className="a1-async-reveal">{children}</div>}
      </div>
    </div>
  );
}

export function PanelSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="a1-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => <span key={i} className="a1-skeleton-line" />)}
    </div>
  );
}

export function useReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    update();
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}
